import { router } from "@inertiajs/react";
import { LoaderIcon, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/lib/utils";
import type { IncentiveSummary } from "@/types/admin-portal/bonus-ro";
import {
	type AdjustmentDirection,
	adjustmentAmount,
	formatSigned,
} from "./balances";
import { sendWrite } from "./rules";

const DIRECTIONS: AdjustmentDirection[] = ["add", "subtract"];

/**
 * A manual ± correction to one therapist's balance. Saving asks for confirmation first:
 * the ledger is append-only, so a correction is undone only by an opposite one.
 */
export function AdjustmentDialog({
	therapist,
	url,
	onClose,
}: {
	therapist: IncentiveSummary;
	url: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("bonus-ro");
	const [direction, setDirection] = useState<AdjustmentDirection>("add");
	const [amountText, setAmountText] = useState("");
	const [reason, setReason] = useState("");
	const [confirming, setConfirming] = useState(false);
	const [saving, setSaving] = useState(false);

	const amount = adjustmentAmount(direction, amountText);
	const amountInvalid = amountText !== "" && amount === null;
	const canContinue = amount !== null && reason.trim() !== "";
	const name = therapist.therapistName ?? therapist.therapistId;

	async function onSave() {
		setSaving(true);
		const result = await sendWrite(
			url,
			"POST",
			{ therapist_id: therapist.therapistId, amount, reason: reason.trim() },
			t("adjustment.error"),
		);
		setSaving(false);
		if (!result.ok) {
			toast.error(result.error);
			setConfirming(false);
			return;
		}
		toast.success(t("adjustment.saved", { name }));
		onClose();
		router.reload({ only: ["therapists"] });
	}

	return (
		<Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("adjustment.title", { name })}</DialogTitle>
					<DialogDescription>
						{t("adjustment.description", {
							balance: formatCurrency(therapist.balance),
						})}
					</DialogDescription>
				</DialogHeader>

				{confirming && amount !== null ? (
					<div className="flex flex-col gap-3">
						<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
							<dt className="text-muted-foreground">
								{t("adjustment.amount")}
							</dt>
							<dd
								className={
									amount < 0
										? "font-medium tabular-nums text-destructive"
										: "font-medium tabular-nums"
								}
							>
								{formatSigned(amount)}
							</dd>
							<dt className="text-muted-foreground">
								{t("adjustment.reason")}
							</dt>
							<dd className="whitespace-pre-wrap break-words">
								{reason.trim()}
							</dd>
						</dl>
						<Alert variant="destructive">
							<TriangleAlert className="size-4" />
							<AlertDescription>{t("adjustment.confirm")}</AlertDescription>
						</Alert>
					</div>
				) : (
					<div className="flex flex-col gap-4">
						<RadioGroup
							value={direction}
							onValueChange={(value) =>
								setDirection(value as AdjustmentDirection)
							}
							orientation="horizontal"
							className="flex gap-6"
						>
							{DIRECTIONS.map((value) => (
								<div key={value} className="flex items-center gap-2">
									<RadioGroupItem
										value={value}
										id={`bonus-ro-adjust-${value}`}
									/>
									<Label
										htmlFor={`bonus-ro-adjust-${value}`}
										className="font-normal"
									>
										{t(`adjustment.directions.${value}`)}
									</Label>
								</div>
							))}
						</RadioGroup>

						<div className="flex flex-col gap-2">
							<Label htmlFor="bonus-ro-adjust-amount">
								{t("adjustment.amount")}
							</Label>
							<Input
								className="bg-input shadow-inner"
								id="bonus-ro-adjust-amount"
								inputMode="numeric"
								value={amountText}
								placeholder="50000"
								aria-invalid={amountInvalid}
								onChange={(e) => setAmountText(e.target.value)}
							/>
							<p
								className={
									amountInvalid
										? "text-xs text-destructive"
										: "text-xs text-muted-foreground"
								}
							>
								{amount === null
									? t("adjustment.amount_hint")
									: formatSigned(amount)}
							</p>
						</div>

						<div className="flex flex-col gap-2">
							<Label htmlFor="bonus-ro-adjust-reason">
								{t("adjustment.reason")}
							</Label>
							<Textarea
								className="bg-input shadow-inner"
								id="bonus-ro-adjust-reason"
								value={reason}
								rows={3}
								onChange={(e) => setReason(e.target.value)}
							/>
							<p className="text-xs text-muted-foreground">
								{t("adjustment.reason_hint")}
							</p>
						</div>
					</div>
				)}

				<DialogFooter>
					{confirming ? (
						<>
							<Button
								variant="outline"
								onClick={() => setConfirming(false)}
								disabled={saving}
							>
								{t("actions.back")}
							</Button>
							<Button onClick={onSave} disabled={saving}>
								{saving && <LoaderIcon className="size-4 animate-spin" />}
								{t("adjustment.submit")}
							</Button>
						</>
					) : (
						<>
							<Button variant="outline" onClick={onClose}>
								{t("actions.cancel")}
							</Button>
							<Button
								onClick={() => setConfirming(true)}
								disabled={!canContinue}
							>
								{t("actions.continue")}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
